import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import { OpenPositionsColumns, OpenOrdersColumns, OrderHistoryColumns } from "./DashboardInfo";
import OpenOrders from "./OpenOrders";
import OpenPositions from "./OpenPositions";
import OrderHistory from "./OrderHistory";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { getUserPositions } from "../../redux/slices/tradeOrdersSlice";

export const isMultiLegOrder = order =>
  order.type === "multileg" || order.class === "multileg";
export const isLimitOrder = order =>
  order.type === "otoco" || order.type === "oto";

const tableType = {
  openPositions: 0,
  openOrders: 1,
  orderHistory: 2,
};
const columnMapping = {
  [tableType.openPositions]: OpenPositionsColumns,
  [tableType.openOrders]: OpenOrdersColumns,
  [tableType.orderHistory]: OrderHistoryColumns,
};
function OrdersTable({ value, navigateTab }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rows] = useState([]);
  const [openOrdersSymbols, setOpenOrdersSymbols] = useState([]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { orders, trades } = useSelector((state: { tradeOrders }) => state.tradeOrders);
  const dispatch = useDispatch<AppDispatch>();
  const virtualAccount = useSelector(
    (state: { tradierAccount }) => state.tradierAccount.account,
  );

  useEffect(() => {
    const fetchPositions = async () => {
      if (virtualAccount?.external_id) {
        const { payload } = await dispatch(getUserPositions({
          vendor: "tradier",
          accountId: virtualAccount.external_id,
        }));

        const symbols = payload.map(position => position.symbol);
        setOpenOrdersSymbols(symbols);
      }
    };

    fetchPositions();
  }, [dispatch, virtualAccount?.external_id]);

  const findTradeByOrderId = (orderId) => {
    const trade = trades.find(trade => trade.order_id === orderId);
    return trade.trade_idea[0];
  };

  const filledOrders = orders.filter((order) => {
    if (order.option_symbol) {
      const filledSingleLeg = openOrdersSymbols.includes(order.option_symbol);
      return filledSingleLeg;
    }

    if (order.option_symbol === null && Array.isArray(order.legs)) {
      const filledMultiLeg = order.legs.every(leg => openOrdersSymbols.includes(leg.option_symbol));
      return filledMultiLeg;
    }
    return false;
  });

  const filledOrderIds = new Set(filledOrders.map(order => order.id));

  const pendingOrders = orders.filter(order =>
    !filledOrderIds.has(order.id),
  );
  const historyOrders = orders.filter(order => order.status !== "pending" && order.status !== "open");

  const bodyMapper = {
    [tableType.openPositions]: (
      <OpenPositions
        filledOrders={filledOrders}
        findTradeByOrderId={findTradeByOrderId}
      />
    ),
    [tableType.openOrders]: (
      <OpenOrders
        pendingOrders={pendingOrders}
        findTradeByOrderId={findTradeByOrderId}
        navigateTab={navigateTab}
      />
    ),
    [tableType.orderHistory]: (
      <OrderHistory
        historyOrders={historyOrders}
        findTradeByOrderId={findTradeByOrderId}
        navigateTab={navigateTab}
      />
    ),
  };
  const columns = columnMapping[value] || [];
  const bodyContent = bodyMapper[value] || null;
  return (
    <div className="flex flex-col flex-grow overflow-hidden">
      <TableContainer className="flex-grow overflow-y-auto h-[500px]">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  sx={column.width ? { width: column.width } : {}}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className="tabular-nums">
            {bodyContent}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        className="p-3 overflow-visible overscroll-none"
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}

export default OrdersTable;
